import { GenerationKind, GenerationRow } from './types';

// Which of the two frameworks produced a row, and how to say so in one line.
//
// The server sends `kind` explicitly; this never re-derives it from a null
// source ad. A LEGACY row carries BOTH a source ad and a layout key — that is a
// remake which also filled a template, not a structured run — so no single
// column can be read as the answer.

export function rowKind(row: Pick<GenerationRow, 'kind'>): GenerationKind {
  return row.kind === 'format' ? 'format' : 'remake';
}

export function isStructured(row: Pick<GenerationRow, 'kind'>): boolean {
  return rowKind(row) === 'format';
}

/**
 * The card/table subtitle: where this creative came from.
 *
 * A structured run has no advertiser to name, and "from unknown ad" would be
 * both wrong and alarming — there was never an ad. It names its format instead.
 */
export function originLine(
  row: Pick<GenerationRow, 'kind' | 'formatKey' | 'advertiserName' | 'pageName'>,
  formatLabel: (key?: string | null) => string,
): string {
  if (rowKind(row) === 'format') {
    const label = formatLabel(row.formatKey);
    // Loud rather than blank: a structured row with no format key is a bug, and
    // saying so beats an empty line nobody can act on.
    return label ? `${label} format` : 'Structured — no format recorded';
  }
  return `from ${row.advertiserName || row.pageName || 'unknown ad'}`;
}

/**
 * The subtitle for a folded stack. Grouping is manual, so a stack CAN span both
 * frameworks — say that rather than picking the leader's answer and implying it
 * holds for all of them.
 */
export function groupOriginLine(
  rows: Array<Pick<GenerationRow, 'kind' | 'formatKey' | 'advertiserName' | 'pageName'>>,
  formatLabel: (key?: string | null) => string,
): string {
  if (!rows.length) return '';
  const kinds = new Set(rows.map(rowKind));
  if (kinds.size > 1) return 'Mixed sources';
  return originLine(rows[0], formatLabel);
}
